import React from 'react'
import type { ChangeEvent, Dispatch, SetStateAction, SyntheticEvent } from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import { useTranslation } from 'react-i18next'

import PriceSlider from './price-slider'
import HeightSlider from './height-slider'
import SortingSelector from './sorting-selector'
import FullGarageToggle from './full-garage-toggle'
import type { FiltersType, Currency, SortBy, ParkingType, MinMaxRange } from '../types'
import type { SliderProps } from '@mui/joy'
import WidthSlider from './width-slider'
import LengthSlider from './length-slider'
import WeightSlider from './weight-slider'
import ParkingTypeSelector from './parking-type-selector'
import GarageRestrictionsToggle from './garage-restrictions-toggle'

export default function Filters({
    currentFilters,
    setCurrentFilters,
    results,
    priceRange,
    heightRange,
    widthRange,
    lengthRange,
    weightRange,
    currency,
    handleFiltersReset,
    handleTabChange,
    desktop,
    handleToggleDesktopFilters,
}: {
    currentFilters: FiltersType
    setCurrentFilters: Dispatch<SetStateAction<FiltersType>>
    results: number
    priceRange: MinMaxRange | null,
    heightRange: MinMaxRange | null,
    widthRange: MinMaxRange | null,
    lengthRange: MinMaxRange | null,
    weightRange: MinMaxRange | null,
    currency: Currency
    handleFiltersReset: () => void
    handleTabChange: (
        _event: SyntheticEvent<Element, Event> | null,
        newValue: string | number | null
    ) => void
    desktop: boolean
    handleToggleDesktopFilters: () => void
}) {
    const { t } = useTranslation()

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target

        setCurrentFilters(prev => ({
            ...prev,
            [name]: value,
        }))
    }

    const handleChecked = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target

        setCurrentFilters(prevState => ({
            ...prevState,
            [name]: checked,
        }))
    }

    const handleSlidePriceMinMax: SliderProps['onChange'] = (_event, value) => {
        if (Array.isArray(value)) {
            setCurrentFilters(prevState => ({
                ...prevState,
                minPrice: value[0],
                maxPrice: value[1],
            }))
        }
    }

    const handleHeightSliderChange: SliderProps['onChange'] = (_event, value) => {
        if (typeof value === 'number') {
            setCurrentFilters(prev => ({
                ...prev,
                minHeight: value,
            }))
        }
    }

    const handleWidthSliderChange: SliderProps['onChange'] = (_event, value) => {
        if (typeof value === 'number') {
            setCurrentFilters(prev => ({
                ...prev,
                minWidth: value,
            }))
        }
    }

    const handleLengthSliderChange: SliderProps['onChange'] = (_event, value) => {
        if (typeof value === 'number') {
            setCurrentFilters(prev => ({
                ...prev,
                minLength: value,
            }))
        }
    }

    const handleWeightSliderChange: SliderProps['onChange'] = (_event, value) => {
        if (typeof value === 'number') {
            setCurrentFilters(prev => ({
                ...prev,
                minWeight: value,
            }))
        }
    }

    const handleSortChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target
        setCurrentFilters(prev => ({
            ...prev,
            sort: value as SortBy,
        }))
    }

    const handleParkingTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target
        setCurrentFilters(prev => ({
            ...prev,
            parkingType: value as ParkingType,
        }))
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            overflowX: 'hidden',
            p: 2,
            gap: 3,
        }}>
            <PriceSlider
                currency={currency === 'eur' ? 'EUR' : 'CZK'}
                minValue={currentFilters.minPrice}
                maxValue={currentFilters.maxPrice}
                range={priceRange}
                handleSlideMinMax={handleSlidePriceMinMax}
                handleChange={handleInputChange}
            />
            <HeightSlider
                value={currentFilters.minHeight}
                handleChange={handleHeightSliderChange}
                range={heightRange}
            />
            <WidthSlider
                value={currentFilters.minWidth}
                handleChange={handleWidthSliderChange}
                range={widthRange}
            />
            <LengthSlider
                value={currentFilters.minLength}
                handleChange={handleLengthSliderChange}
                range={lengthRange}
            />
            <WeightSlider
                value={currentFilters.minWeight}
                handleChange={handleWeightSliderChange}
                range={weightRange}
            />
            <ParkingTypeSelector value={currentFilters.parkingType} handleChange={handleParkingTypeChange} />
            <SortingSelector value={currentFilters.sort} handleChange={handleSortChange} />
            <GarageRestrictionsToggle currentFilters={currentFilters} handleChecked={handleChecked} />
            <FullGarageToggle value={currentFilters.showAvailable} handleChecked={handleChecked} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button variant="outlined" onClick={handleFiltersReset}>
                    {t('filters.reset')}
                </Button>
                <Button
                    variant="solid"
                    onClick={
                        desktop ? () => handleToggleDesktopFilters() : e => handleTabChange(e, 0)
                    }
                >
                    {t('filters.showResults')} {`(${results})`}
                </Button>
            </Box>
        </Box>
    )
}
